/**
 * AdminArticleEditor — create/edit a WIM Blog or Care News article
 * (Aug 2026 self-service Articles admin).
 *
 * Body format: stored as HTML (authored via the Quill WYSIWYG editor
 * below). The 31 articles that existed before this editor are stored as
 * plain text — when one of those is opened here, its body is converted
 * to simple paragraphs purely for display in the editor; nothing is
 * written back to the database until the admin actually clicks Save,
 * so opening-without-saving never changes anything. See
 * ArticleBody.jsx for how the public pages render either format.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { ArrowLeft, Image as ImageIcon, X, Loader2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { PageHero } from "../../components/shared/Primitives";
import { BLOG_CATEGORIES_FOR_ADMIN, NEWS_CATEGORIES_FOR_ADMIN } from "../../lib/articleCategories";
import { londonLocalToUtcIso, utcIsoToLondonParts } from "../../lib/ukTime";

const SECTION_URL_SEGMENT = { wim_blog: "wim-blog", care_news: "care-news", pro_carer_series: "advantage-series" };
const SECTION_PARAM_TO_SECTION = { "care-news": "care_news", "advantage-series": "pro_carer_series" };
const SECTION_SLUG_PREFIX = {
  wim_blog: "/wim-blog/",
  care_news: "/care-news/",
  pro_carer_series: "/workspace/pro-carer-circle/advantage-series/",
};

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "link"],
    ["clean"],
  ],
};
const QUILL_FORMATS = ["header", "bold", "italic", "list", "bullet", "blockquote", "link"];

function slugify(text) {
  let s = (text || "").trim().toLowerCase().replace(/[^\w\s-]/g, "");
  s = s.replace(/[\s_-]+/g, "-").replace(/^-+|-+$/g, "");
  return s;
}

const HTML_TAG_RE = /<[a-z][\s\S]*>/i;

function escapeHtml(text) {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Legacy plain-text bodies have no HTML tags at all — convert to simple
// paragraphs so they display correctly inside Quill. Purely a display
// convenience; the database is untouched unless the admin clicks Save.
function toEditableHtml(body) {
  if (!body) return "";
  if (HTML_TAG_RE.test(body)) return body;
  return body.split(/\n\s*\n/).map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br>")}</p>`).join("");
}

const inputCls = "mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal/30";

export default function AdminArticleEditor() {
  const { section: sectionParam, slug: slugParam } = useParams();
  const navigate = useNavigate();
  const isEditing = !!slugParam;
  const isNews = sectionParam === "care-news";

  const [section, setSection] = useState(SECTION_PARAM_TO_SECTION[sectionParam] || "wim_blog"); // fixed once editing; chosen on create
  const [loaded, setLoaded] = useState(!isEditing);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [slugLocked, setSlugLocked] = useState(isEditing);
  const [slugTouched, setSlugTouched] = useState(false);
  const fileInputRef = useRef(null);

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [category, setCategory] = useState("");
  const [author, setAuthor] = useState("JCC Editorial"); // WIM Blog only
  const [source, setSource] = useState(""); // Care News only
  const [trafficLight, setTrafficLight] = useState(""); // Care News only
  const [publishedAt, setPublishedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [excerpt, setExcerpt] = useState("");
  const [body, setBody] = useState("");
  const [status, setStatus] = useState("draft");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [featuredImage, setFeaturedImage] = useState(null);
  const [passthrough, setPassthrough] = useState({}); // is_featured/pinned_note/comparison_table/faq_items/tool_cta — preserved unchanged

  const isBlog = section !== "care_news";
  const isSeries = section === "pro_carer_series";
  const categories = section === "wim_blog" ? BLOG_CATEGORIES_FOR_ADMIN : section === "care_news" ? NEWS_CATEGORIES_FOR_ADMIN : null;

  useEffect(() => {
    if (!isEditing) return;
    let alive = true;
    (async () => {
      try {
        const endpoint = isNews ? `/api/admin/news/${slugParam}` : `/api/admin/blog/${slugParam}`;
        const { data } = await api.get(endpoint);
        if (!alive) return;
        setTitle(data.title || "");
        setSlug(data.slug || slugParam);
        setCategory(data.category || "");
        setAuthor(data.author || "JCC Editorial");
        setSource(data.source || "");
        setTrafficLight(data.traffic_light || "");
        setPublishedAt((data.published_at || "").slice(0, 10) || new Date().toISOString().slice(0, 10));
        setExcerpt(data.excerpt || data.summary || "");
        setBody(toEditableHtml(data.body));
        setStatus(data.status || "published");
        const sp = utcIsoToLondonParts(data.scheduled_publish_at);
        setScheduledDate(sp.date);
        setScheduledTime(sp.time);
        setFeaturedImage(data.featured_image || null);
        setPassthrough({
          is_featured: data.is_featured,
          pinned_note: data.pinned_note,
          comparison_table: data.comparison_table,
          faq_items: data.faq_items,
          tool_cta: data.tool_cta,
        });
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Could not load this article.");
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [isEditing, isNews, slugParam]);

  const onTitleChange = (e) => {
    const v = e.target.value;
    setTitle(v);
    if (!isEditing && !slugTouched) setSlug(slugify(v));
  };

  const onImageSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingImage(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/api/admin/articles/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFeaturedImage(data.featured_image);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not upload this image.");
    } finally {
      setUploadingImage(false);
    }
  };

  const save = useCallback(async () => {
    if (!title.trim() || !category || !body.trim() || (isBlog && !excerpt.trim()) || (!isBlog && !excerpt.trim())) {
      toast.error("Please fill in the title, category, excerpt/summary and article body.");
      return;
    }
    let scheduledPublishAtIso = null;
    if (status === "scheduled") {
      if (!scheduledDate || !scheduledTime) {
        toast.error("Please choose a date and time to schedule this article for.");
        return;
      }
      scheduledPublishAtIso = londonLocalToUtcIso(scheduledDate, scheduledTime);
      if (new Date(scheduledPublishAtIso) <= new Date()) {
        toast.error("The scheduled time must be in the future.");
        return;
      }
    }
    setSaving(true);
    try {
      const publishedAtIso = publishedAt ? new Date(`${publishedAt}T00:00:00Z`).toISOString() : undefined;
      let resp;
      if (isBlog) {
        const payload = {
          title, body, category, author, excerpt, status,
          scheduled_publish_at: scheduledPublishAtIso,
          published_at: publishedAtIso,
          featured_image: featuredImage,
          section,
          slug: (isEditing && !slugLocked) || (!isEditing && slugTouched) ? slug : undefined,
          ...passthrough,
        };
        resp = isEditing
          ? await api.put(`/api/admin/blog/${slugParam}`, payload)
          : await api.post("/api/admin/blog", payload);
      } else {
        const payload = {
          title, body, category, source: source || undefined, traffic_light: trafficLight || undefined,
          summary: excerpt, status,
          scheduled_publish_at: scheduledPublishAtIso,
          published_at: publishedAtIso,
          featured_image: featuredImage,
          slug: (isEditing && !slugLocked) || (!isEditing && slugTouched) ? slug : undefined,
        };
        resp = isEditing
          ? await api.put(`/api/admin/news/${slugParam}`, payload)
          : await api.post("/api/admin/news", payload);
      }
      toast.success(isEditing ? "Article saved." : "Article created.");
      if (!isEditing) {
        navigate(`/admin/articles/${SECTION_URL_SEGMENT[section]}/${resp.data.slug}/edit`);
      } else if (resp.data.slug !== slugParam) {
        navigate(`/admin/articles/${SECTION_URL_SEGMENT[section]}/${resp.data.slug}/edit`);
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not save this article.");
    } finally {
      setSaving(false);
    }
  }, [title, body, category, author, excerpt, status, scheduledDate, scheduledTime, publishedAt, featuredImage, slug, slugLocked, slugTouched,
      isBlog, section, isEditing, slugParam, source, trafficLight, passthrough, navigate]);

  if (isEditing && !loaded) {
    return <div className="container-jcc py-24 text-center text-ink-muted" data-testid="admin-article-editor-loading">Loading article…</div>;
  }

  return (
    <div data-testid="admin-article-editor-page">
      <PageHero
        eyebrow="JCC Admin"
        title={isEditing ? "Edit article" : "New article"}
        description="Formatting entered here (headings, bold, italics, lists, links, quotes) displays exactly the same way on the public article page."
      >
        <Link to="/admin/articles" className="btn-outline text-sm" data-testid="admin-article-editor-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Articles
        </Link>
      </PageHero>

      <section className="section pt-8 pb-24">
        <div className="container-jcc max-w-3xl">
          <div className="card-base p-6 sm:p-8 space-y-5">
            {!isEditing && (
              <div>
                <span className="text-sm font-medium text-ink">Section</span>
                <div className="mt-2 flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => { setSection("wim_blog"); setCategory(""); }}
                    className={`pill text-sm ${section === "wim_blog" ? "bg-purple text-white" : "bg-slate-100 text-ink-muted"}`}
                    data-testid="admin-article-section-wim-blog"
                  >
                    WIM Blog
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSection("care_news"); setCategory(""); }}
                    className={`pill text-sm ${isNews ? "bg-teal text-white" : "bg-slate-100 text-ink-muted"}`}
                    data-testid="admin-article-section-care-news"
                  >
                    Care News
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSection("pro_carer_series"); setCategory(""); }}
                    className={`pill text-sm ${isSeries ? "bg-amber-600 text-white" : "bg-slate-100 text-ink-muted"}`}
                    data-testid="admin-article-section-advantage-series"
                  >
                    Advantage Series (Pro Carer Office)
                  </button>
                </div>
              </div>
            )}

            <label className="block">
              <span className="text-sm font-medium text-ink">Title</span>
              <input value={title} onChange={onTitleChange} className={inputCls} data-testid="admin-article-title-input" />
            </label>

            <div>
              <span className="text-sm font-medium text-ink">URL / slug</span>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-xs text-ink-soft shrink-0">{SECTION_SLUG_PREFIX[section]}</span>
                <input
                  value={slug}
                  onChange={(e) => { setSlug(slugify(e.target.value)); setSlugTouched(true); }}
                  disabled={slugLocked}
                  className={`${inputCls} mt-0 ${slugLocked ? "bg-slate-50 text-ink-muted" : ""}`}
                  data-testid="admin-article-slug-input"
                />
                {slugLocked && (
                  <button
                    type="button"
                    onClick={() => setSlugLocked(false)}
                    className="text-xs text-teal font-medium shrink-0 whitespace-nowrap"
                    data-testid="admin-article-unlock-slug-btn"
                  >
                    Change URL
                  </button>
                )}
              </div>
              {isEditing && !slugLocked && (
                <p className="mt-1 text-xs text-amber-700">
                  Changing the URL will break any existing links or bookmarks to this article.
                </p>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-sm font-medium text-ink">Category</span>
                {isSeries ? (
                  <input
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder="e.g. Business Growth"
                    className={inputCls}
                    data-testid="admin-article-category-input"
                  />
                ) : (
                  <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls} data-testid="admin-article-category-select">
                    <option value="">Select…</option>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                )}
              </label>
              <label className="block">
                <span className="text-sm font-medium text-ink">Publish date</span>
                {status === "scheduled" ? (
                  <p className="mt-1 text-sm text-ink-soft py-2">Set automatically from your schedule below.</p>
                ) : (
                  <input type="date" value={publishedAt} onChange={(e) => setPublishedAt(e.target.value)} className={inputCls} data-testid="admin-article-published-at-input" />
                )}
              </label>
            </div>

            {isBlog ? (
              <label className="block">
                <span className="text-sm font-medium text-ink">Author</span>
                <input value={author} onChange={(e) => setAuthor(e.target.value)} className={inputCls} data-testid="admin-article-author-input" />
              </label>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-sm font-medium text-ink">Source (optional)</span>
                  <input value={source} onChange={(e) => setSource(e.target.value)} className={inputCls} data-testid="admin-article-source-input" />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-ink">Traffic light (optional)</span>
                  <select value={trafficLight} onChange={(e) => setTrafficLight(e.target.value)} className={inputCls} data-testid="admin-article-traffic-light-select">
                    <option value="">None</option>
                    <option value="green">Green</option>
                    <option value="amber">Amber</option>
                    <option value="red">Red</option>
                  </select>
                </label>
              </div>
            )}

            <label className="block">
              <span className="text-sm font-medium text-ink">{isBlog ? "Excerpt" : "Summary"}</span>
              <textarea
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                rows={2}
                maxLength={isBlog ? 400 : 600}
                className={inputCls}
                data-testid="admin-article-excerpt-input"
              />
              <span className="text-xs text-ink-soft">Shown on the listing card. {excerpt.length}/{isBlog ? 400 : 600}</span>
            </label>

            <div>
              <span className="text-sm font-medium text-ink">Featured image</span>
              <p className="text-xs text-ink-soft mt-0.5">Landscape 16:9, roughly 1200×675px works best. Resized automatically.</p>
              <div className="mt-2 flex items-center gap-3">
                {featuredImage ? (
                  <div className="relative w-40 aspect-[16/9] rounded-lg overflow-hidden border border-slate-200">
                    <img src={featuredImage} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setFeaturedImage(null)}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center"
                      data-testid="admin-article-remove-image-btn"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="w-40 aspect-[16/9] rounded-lg border border-dashed border-slate-300 flex flex-col items-center justify-center gap-1 text-ink-soft hover:border-teal hover:text-teal transition-colors"
                    data-testid="admin-article-upload-image-btn"
                  >
                    {uploadingImage ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImageIcon className="w-5 h-5" strokeWidth={1.75} />}
                    <span className="text-xs">{uploadingImage ? "Uploading…" : "Upload image"}</span>
                  </button>
                )}
                {featuredImage && (
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="text-sm text-teal font-medium" data-testid="admin-article-replace-image-btn">
                    Replace image
                  </button>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onImageSelected} className="hidden" data-testid="admin-article-image-file-input" />
            </div>

            <div>
              <span className="text-sm font-medium text-ink">Article body</span>
              <div className="mt-1" data-testid="admin-article-body-editor">
                <ReactQuill theme="snow" value={body} onChange={setBody} modules={QUILL_MODULES} formats={QUILL_FORMATS} />
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100 space-y-3">
              <span className="text-sm font-medium text-ink">Publishing</span>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setStatus("draft")}
                  className={`pill text-sm ${status === "draft" ? "bg-ink text-white" : "bg-slate-100 text-ink-muted"}`}
                  data-testid="admin-article-status-draft-btn"
                >
                  Save as Draft
                </button>
                <button
                  type="button"
                  onClick={() => setStatus("published")}
                  className={`pill text-sm ${status === "published" ? "bg-green-600 text-white" : "bg-slate-100 text-ink-muted"}`}
                  data-testid="admin-article-status-published-btn"
                >
                  Publish Now
                </button>
                <button
                  type="button"
                  onClick={() => setStatus("scheduled")}
                  className={`pill text-sm ${status === "scheduled" ? "bg-amber-600 text-white" : "bg-slate-100 text-ink-muted"}`}
                  data-testid="admin-article-status-scheduled-btn"
                >
                  Schedule for Later
                </button>
              </div>
              <p className="text-xs text-ink-soft">
                {status === "published" && "Visible on the public site as soon as you save."}
                {status === "draft" && "Not visible publicly until published."}
                {status === "scheduled" && "Hidden from readers everywhere until the scheduled time, then goes live automatically."}
              </p>
              {status === "scheduled" && (
                <div className="grid sm:grid-cols-2 gap-3 rounded-lg bg-amber-50 border border-amber-200 p-4">
                  <label className="block">
                    <span className="text-xs font-medium text-ink">Publish date (UK time)</span>
                    <input
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      className={inputCls}
                      data-testid="admin-article-scheduled-date-input"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-ink">Publish time (UK time)</span>
                    <input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      className={inputCls}
                      data-testid="admin-article-scheduled-time-input"
                    />
                  </label>
                  <p className="sm:col-span-2 text-xs text-amber-800">
                    Times are UK local — GMT/BST is handled automatically. The article will appear
                    in {section === "wim_blog" ? "the WIM Blog" : section === "care_news" ? "Care News" : "the Pro Carer Blog"} the moment this time arrives.
                  </p>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="btn-teal w-full justify-center"
              data-testid="admin-article-save-btn"
            >
              {saving
                ? "Saving…"
                : status === "published"
                ? "Publish Now"
                : status === "scheduled"
                ? "Schedule Article"
                : "Save as Draft"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
